// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { connect } from 'react-redux';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import { RootState } from 'client/core/store';
import * as authActions from 'common/modules/auth/actions';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import * as formErrorsActions from 'common/modules/app/formErrors/actions';
import {
    Redirect,
    RouteComponentProps,
    withRouter,
} from 'react-router';
import { LoaderBar } from 'common/components';
import {
    requiredRule,
    validate,
} from 'common/validator';
import {
    FieldWithLoader,
    InfoBlock,
    LinkText,
} from 'common/modules/auth/containers/ForgotPassword/Styles';
import { StyledForm } from 'common/modules/auth/components/StyledForm';
import {
    Button,
    Form,
    FormFieldText,
    Section,
    Translate,
} from '@plesk/ui-library';
import { NetworkErrorText } from 'common/modules/auth/components/NetworkErrorText';
import { PasswordLabel } from 'common/modules/auth/components/PasswordLabel/PasswordLabel';
import LogoBox from 'common/modules/auth/components/LogoBox/LogoBox';
import { pathTo } from 'common/helpers/core';
import { IResetPasswordRequest } from 'common/api/resources/Auth';
import {
    AuthContainer,
    Header,
    Link,
} from 'common/modules/auth/Styles';
import { Link as RouterLink } from 'react-router-dom';

export type ForgotPasswordProps =
    RouteComponentProps &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

// interval in seconds
const RESET_INTERVAL = 80;

const defaultValues = {
    email: '',
};

export const ForgotPassword: React.FC<ForgotPasswordProps> = ({
    authActions: actions,
    errors,
    history,
    isLinkRequested,
    formErrorsActions: { setFormErrors },
    responseError,
    auth,
}) => {
    const [submitValues] = React.useState({ ...defaultValues });
    const [elapsed, setElapsed] = React.useState(0);

    React.useEffect(() => () => {
        setFormErrors({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleReset = async(values: IResetPasswordRequest) => {
        setFormErrors({});

        const validationErrors = validate(values, {
            email: requiredRule(<Translate content="validate.fieldRequired" />),
        });

        if (Object.keys(validationErrors).length) {
            setFormErrors(validationErrors);
            return;
        }

        try {
            if (elapsed > 0 || isLinkRequested) {
                return;
            }

            setElapsed(RESET_INTERVAL);
            await actions.resetPassword(values);
        } catch (e) {
            setElapsed(0);
            throw e;
        }
    };

    if (auth.isAuth) {
        return (<Redirect to="/" />);
    }

    return (
        <AuthContainer>
            <LogoBox />
            <Header>
                <Translate content="auth.forgotPassword"/>
            </Header>
            <StyledForm>
                <Form
                    onSubmit={handleReset}
                    applyButton={false}
                    values={submitValues}
                    vertical={true}
                    errors={errors}
                    cancelButton={false}
                    hideRequiredLegend={true}
                    submitButton={false}
                >
                    <Section>
                        <FieldWithLoader>
                            <FormFieldText
                                size="fill"
                                name="email"
                                label={<PasswordLabel elapsed={elapsed} setElapsed={setElapsed}/>}
                                required={false}
                            />
                            <LoaderBar isLoading={isLinkRequested} />
                            <InfoBlock>
                                {elapsed > 0 && (
                                    <LinkText>
                                        <Translate content="auth.linkSent"/>
                                    </LinkText>
                                )}
                                <NetworkErrorText error={responseError}/>
                            </InfoBlock>
                        </FieldWithLoader>
                        <Button
                            disabled={elapsed > 0}
                            fill={true}
                            intent="primary"
                            type="submit"
                            state={isLinkRequested ? 'loading' : undefined}
                        >
                            <Translate content="auth.sendRecoveryLink"/>
                        </Button>
                        <Link>
                            <Button
                                component={RouterLink}
                                to={pathTo('')}
                                ghost={true}
                            >
                                <Translate content="auth.signIn"/>
                            </Button>
                        </Link>
                    </Section>
                </Form>
            </StyledForm>
        </AuthContainer>
    );
};

const mapStateToProps = (state: RootState) => ({
    auth: state.auth,
    errors: state.app.formErrors,
    responseError: state.app.responseError,
    isLinkRequested: state.app.loadingFlags.has(LOADING_FLAGS.PASSWORD_RESET_LINK),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    authActions: bindActionCreators(authActions, dispatch),
    formErrorsActions: bindActionCreators(formErrorsActions, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ForgotPassword));
